import React, { useMemo } from 'react'
import { DollarSign, Gauge, Calendar } from 'lucide-react'
import { PriceInput, MileageInput } from '../../form'
import { Accordion, AccordionItem } from '../../ui'
import { LeadFormField, LeadSelectField, LeadCheckboxField } from '../form'
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Label } from '../../ui/label'
import { Badge } from '../../ui/Badge'
import { LeadInterest } from '../../../types'
import { formatCurrency, formatMileage } from '../../../utils/formatters'
import { getVehicleFeatureGroupsForCategory } from '../../../hooks/useVehicleOptions'

const LEAD_FEATURE_CATEGORIES = [
  { key: 'comfort', label: 'Conforto' },
  { key: 'safety', label: 'Segurança' },
  { key: 'technology', label: 'Tecnologia' },
  { key: 'convenience', label: 'Conveniência' },
  { key: 'performance', label: 'Performance' },
  { key: 'lighting', label: 'Iluminação' },
  { key: 'adas', label: 'ADAS' },
] as const

interface PreferencesTabProps {
 interest: LeadInterest
 minPriceFormatted: string
 maxPriceFormatted: string
 minMileageFormatted: string
 maxMileageFormatted: string
 vehicleOptions: any
 onInterestChange: (field: keyof LeadInterest, value: any) => void
 onMinPriceChange: (value: string) => void
 onMaxPriceChange: (value: string) => void
 onMinMileageChange: (value: string) => void
 onMaxMileageChange: (value: string) => void
 onPriceSuggestion: (value: number) => void
 onMileageSuggestion: (value: number) => void
 onMultiSelectChange: (field: keyof LeadInterest, value: string) => void
}

export const PreferencesTab: React.FC<PreferencesTabProps> = ({
 interest,
 minPriceFormatted,
 maxPriceFormatted,
 minMileageFormatted,
 maxMileageFormatted,
 vehicleOptions,
 onInterestChange,
 onMinPriceChange,
 onMaxPriceChange,
 onMinMileageChange,
 onMaxMileageChange,
 onPriceSuggestion,
 onMileageSuggestion,
 onMultiSelectChange
}) => {
 const interestFeatureGroups = useMemo(
 () => getVehicleFeatureGroupsForCategory(vehicleOptions, interest.category),
 [vehicleOptions, interest.category]
 )
 const hasLeadFeatureOptions = useMemo(
 () => Object.values(interestFeatureGroups).some(arr => Array.isArray(arr) && arr.length > 0),
 [interestFeatureGroups]
 )

 return (
 <div className="space-y-5">
 {/* Faixa de Preço */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-base font-semibold tracking-tight">
 <DollarSign className="h-4 w-4" />
 Faixa de Preço
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label>Preço Mínimo</Label>
 <PriceInput
 value={minPriceFormatted}
 onChange={onMinPriceChange}
 placeholder="R$ 0,00"
 />
 </div>

 <div className="space-y-2">
 <Label>Preço Máximo</Label>
 <PriceInput
 value={maxPriceFormatted}
 onChange={onMaxPriceChange}
 placeholder="R$ 0,00"
 />
 </div>
 </div>
 
 <div className="mt-4 flex flex-wrap gap-2 items-center">
 <Label className="text-xs text-muted-foreground">Sugestões:</Label>
 {[30000, 40000, 50000, 60000].map((value) => (
 <Button
 key={value}
 type="button"
 variant="outline"
 size="sm"
 onClick={() => onPriceSuggestion(value)}
 >
 Até {formatCurrency(value)}
 </Button>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Faixa de Quilometragem e Ano */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {/* Quilometragem */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-base font-semibold tracking-tight">
 <Gauge className="h-4 w-4" />
 Quilometragem
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label>KM Mín</Label>
 <MileageInput
 value={minMileageFormatted}
 onChange={onMinMileageChange}
 placeholder="0"
 />
 </div>

 <div className="space-y-2">
 <Label>KM Máx</Label>
 <MileageInput
 value={maxMileageFormatted}
 onChange={onMaxMileageChange}
 placeholder="0"
 />
 </div>
 </div>
 
 <div className="mt-4 flex flex-wrap gap-2 items-center">
 <Label className="text-xs text-muted-foreground">Até:</Label>
 {[30000, 50000, 80000].map((value) => (
 <Button
 key={value}
 type="button"
 variant="outline"
 size="sm"
 onClick={() => onMileageSuggestion(value)}
 >
 {formatMileage(value)}
 </Button>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Ano */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-base font-semibold tracking-tight">
 <Calendar className="h-4 w-4" />
 Ano do Veículo
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 gap-3">
 <LeadFormField
 label="Ano Mín"
 type="number"
 min="1900"
 max="2030"
 value={interest.minYear?.toString() || ''}
 onChange={(e) => onInterestChange('minYear', e.target.value ? parseInt(e.target.value) : undefined)}
 placeholder="Ex: 2018"
 />

 <LeadFormField
 label="Ano Máx"
 type="number"
 min="1900"
 max="2030"
 value={interest.maxYear?.toString() || ''}
 onChange={(e) => onInterestChange('maxYear', e.target.value ? parseInt(e.target.value) : undefined)}
 placeholder="Ex: 2024"
 />
 </div>
 
 <div className="mt-4 flex flex-wrap gap-2 items-center">
 <Label className="text-xs text-muted-foreground">De:</Label>
 {[2020, 2022, 2023].map((value) => (
 <Button
 key={value}
 type="button"
 variant="outline"
 size="sm"
 onClick={() => onInterestChange('minYear', value)}
 >
 {value}
 </Button>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Especificações do Veículo */}
 <Card>
 <CardHeader>
 <CardTitle className="text-base font-semibold tracking-tight">Especificações do Veículo</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <LeadSelectField
 label="Categoria"
 value={interest.category || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.categories.map((c: any) => ({ value: c.value, label: c.label }))
 ]}
 onChange={(value) => onInterestChange('category', value)}
 />

 <LeadSelectField
 label="Tipo de Veículo"
 value={interest.type || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.types.map((t: any) => ({ value: t.value, label: t.label }))
 ]}
 onChange={(value) => onInterestChange('type', value)}
 />

 <LeadSelectField
 label="Segmento"
 value={interest.segmentType || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.segmentTypes.map((s: any) => ({ value: s.value, label: s.label }))
 ]}
 onChange={(value) => onInterestChange('segmentType', value)}
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
 <LeadSelectField
 label="Condição"
 value={interest.condition || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.conditions.map((c: any) => ({ value: c.value, label: c.label }))
 ]}
 onChange={(value) => onInterestChange('condition', value)}
 />

 <LeadSelectField
 label="Tipo de Combustível"
 value={interest.fuelType || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.fuelTypes.map((f: any) => ({ value: f.value, label: f.label }))
 ]}
 onChange={(value) => onInterestChange('fuelType', value)}
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
 <LeadSelectField
 label="Transmissão"
 value={interest.transmission || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.transmissions.map((t: any) => ({ value: t.value, label: t.label }))
 ]}
 onChange={(value) => onInterestChange('transmission', value)}
 />

 <LeadSelectField
 label="Tipo de Direção"
 value={interest.steeringType || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.steeringTypes.map((s: any) => ({ value: s.value, label: s.label }))
 ]}
 onChange={(value) => onInterestChange('steeringType', value)}
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
 <LeadSelectField
 label="Cor"
 value={interest.color || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.colors.map((c: any) => ({ value: c.value, label: c.label }))
 ]}
 onChange={(value) => onInterestChange('color', value)}
 />

 <LeadSelectField
 label="Número de Portas"
 value={interest.doors?.toString() || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.doors.map((d: any) => ({ value: d.value.toString(), label: d.label }))
 ]}
 onChange={(value) => onInterestChange('doors', value ? parseInt(value) : undefined)}
 />

 <LeadFormField
 label="Assentos"
 type="number"
 min="2"
 max="9"
 value={interest.seats?.toString() || ''}
 onChange={(e) => onInterestChange('seats', e.target.value ? parseInt(e.target.value) : undefined)}
 placeholder="Ex: 5"
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
 <LeadSelectField
 label="Nível de Blindagem"
 value={interest.armorLevel || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.armorLevels.map((a: any) => ({ value: a.value, label: a.label }))
 ]}
 onChange={(value) => onInterestChange('armorLevel', value)}
 />

 <LeadSelectField
 label="Categoria Especial"
 value={interest.specialCategory || ''}
 options={[
 { value: '', label: 'Selecione...' },
 ...vehicleOptions.specialCategories.map((s: any) => ({ value: s.value, label: s.label }))
 ]}
 onChange={(value) => onInterestChange('specialCategory', value)}
 />
 </div>
 </CardContent>
 </Card>

 {/* Características */}
 {vehicleOptions.characteristics.length > 0 && (
 <Accordion>
 <AccordionItem title={
 <div className="flex items-center justify-between w-full">
 <span>Características do Veículo</span>
 {interest.characteristics && interest.characteristics.length > 0 && (
 <Badge variant="primary" size="sm">
 {interest.characteristics.length} selecionada{interest.characteristics.length !== 1 ? 's' : ''}
 </Badge>
 )}
 </div>
 } defaultOpen={false}>
 <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2">
 {vehicleOptions.characteristics.map((char: any) => (
 <LeadCheckboxField
 key={char.value}
 label={char.label}
 checked={interest.characteristics?.includes(char.value) || false}
 onCheckedChange={() => onMultiSelectChange('characteristics', char.value)}
 containerClassName="hover:bg-muted p-2 rounded transition-colors"
 />
 ))}
 </div>
 </AccordionItem>
 </Accordion>
 )}

 {/* Features por categoria de interesse (A–D) */}
 {hasLeadFeatureOptions && (
 <Accordion>
 <AccordionItem title={
 <div className="flex items-center justify-between w-full">
 <span>Recursos e Equipamentos</span>
 {interest.features && interest.features.length > 0 && (
 <Badge variant="primary" size="sm">
 {interest.features.length} selecionado{interest.features.length !== 1 ? 's' : ''}
 </Badge>
 )}
 </div>
 } defaultOpen={false}>
 <div className="space-y-4">
 {LEAD_FEATURE_CATEGORIES.map(({ key, label }) => {
 const items = interestFeatureGroups[key as keyof typeof interestFeatureGroups] as
 | Array<{ value: string; label: string }>
 | undefined
 if (!items || items.length === 0) return null
 return (
 <Card key={key}>
 <CardHeader>
 <CardTitle className="text-xs uppercase tracking-wide">{label}</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
 {items.map((feature: { value: string; label: string }) => (
 <LeadCheckboxField
 key={feature.value}
 label={feature.label}
 checked={interest.features?.includes(feature.value) || false}
 onCheckedChange={() => onMultiSelectChange('features', feature.value)}
 containerClassName="hover:bg-muted p-1.5 rounded transition-colors"
 />
 ))}
 </div>
 </CardContent>
 </Card>
 )
 })}
 </div>
 </AccordionItem>
 </Accordion>
 )}
 </div>
 )
}

